import React, { useState, useEffect, useRef } from 'react';

export default function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [isVisible, setIsVisible] = useState(false);

  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d66pn5iud6iuf6if0tlg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', service: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const contactInfo = [
    { icon: 'ri-mail-line', label: 'Email', value: 'info@asilivisuals.com', href: 'mailto:info@asilivisuals.com' },
    { icon: 'ri-map-pin-line', label: 'Location', value: 'Kampala, Uganda', href: null },
    { icon: 'ri-global-line', label: 'Service Region', value: 'East Africa', href: null },
  ];

  return (
    <section ref={sectionRef} id="contact" className="relative overflow-hidden">
      {/* CTA Section */}
      <div className="relative">
        <div className="flex flex-col lg:flex-row">
          {/* Left Panel - Visual */}
          <div className="w-full lg:w-[45%] relative min-h-[300px] sm:min-h-[350px] lg:min-h-[700px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20creative%20agency%20workspace%20with%20team%20collaboration%20professional%20office%20environment%20warm%20lighting%20contemporary%20interior%20design%20East%20African%20creative%20professionals%20working%20together%20dynamic%20atmosphere%20premium%20quality%20photography&width=800&height=1000&seq=cta-bg-001&orientation=portrait"
              alt="Creative Team"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F2780C]/95 via-[#F2780C]/90 to-[#F2780C]/80"></div>

            <div className="absolute inset-0 pointer-events-none hidden sm:block">
              <div className="absolute top-12 sm:top-16 left-6 sm:left-8 w-16 sm:w-24 h-16 sm:h-24 border-2 border-white/20 rounded-full"></div>
              <div className="absolute bottom-16 sm:bottom-24 right-6 sm:right-8 w-14 sm:w-20 h-14 sm:h-20 border-2 border-white/20 rotate-45"></div>
            </div>

            <div
              className={`absolute inset-0 flex items-center justify-center px-4 sm:px-8 lg:px-12 py-8 lg:py-0 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="text-center lg:text-left max-w-lg">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  <span className="text-[10px] sm:text-xs font-bold text-white uppercase tracking-wider">Let's Connect</span>
                </div>

                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3 sm:mb-4 leading-[1.1]">
                  Ready to Elevate Your Brand?
                </h2>

                <p className="text-sm sm:text-base lg:text-lg text-white/90 leading-relaxed mb-6 sm:mb-8">
                  Let's develop a strategic communication system that strengthens your credibility and expands your market.
                </p>

                <div className="flex items-center justify-center lg:justify-start gap-4 sm:gap-6">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-black text-white">24h</div>
                    <div className="text-[10px] sm:text-xs text-white/70 uppercase tracking-wider">Response</div>
                  </div>
                  <div className="w-px h-8 sm:h-10 bg-white/30"></div>
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl font-black text-white">100%</div>
                    <div className="text-[10px] sm:text-xs text-white/70 uppercase tracking-wider">Commitment</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Form */}
          <div
            className={`w-full lg:w-[55%] bg-white py-8 sm:py-12 lg:py-16 px-4 sm:px-8 lg:px-12 xl:px-16 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="max-w-xl mx-auto lg:mx-0">
              {/* Contact Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-6 sm:mb-8">
                {contactInfo.map((info) => (
                  <div
                    key={info.label}
                    className="group p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-[#038C7F]/5 hover:shadow-lg transition-all duration-300 cursor-default"
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#038C7F]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2 group-hover:bg-[#038C7F] transition-colors">
                      <i className={`${info.icon} text-base sm:text-lg text-[#038C7F] group-hover:text-white transition-colors`}></i>
                    </div>
                    <div className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{info.label}</div>
                    {info.href ? (
                      <a
                        href={info.href}
                        className="text-xs sm:text-sm font-semibold text-[#0D0D0D] hover:text-[#038C7F] transition-colors cursor-pointer"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <div className="text-xs sm:text-sm font-semibold text-[#0D0D0D]">{info.value}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Form Header */}
              <div className="mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-[#0D0D0D] mb-1">Request a Strategic Consultation</h3>
                <p className="text-xs sm:text-sm text-gray-500">Share your communication objectives and we'll develop a tailored approach.</p>
              </div>

              {/* Consultation Form */}
              <form onSubmit={handleSubmit} data-readdy-form id="consultation-form" className="space-y-3 sm:space-y-4">
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-1.5"
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm transition-all"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-1.5"
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm transition-all"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-1.5"
                    >
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm transition-all"
                      placeholder="Your Company"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="service"
                      className="block text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-1.5"
                    >
                      Service Interest *
                    </label>
                    <select
                      id="service"
                      name="service"
                      required
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm cursor-pointer transition-all"
                    >
                      <option value="">Select a service</option>
                      <option value="Branding & Digital">Branding & Digital Ecosystems</option>
                      <option value="Film & Media">Film & Media Production</option>
                      <option value="Outdoor Advertising">Outdoor & Large-Format Advertising</option>
                      <option value="Garment Manufacturing">Garment Manufacturing & Merchandise</option>
                      <option value="Multiple Services">Multiple Services</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 sm:mb-1.5"
                  >
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#038C7F] text-sm resize-none transition-all"
                    placeholder="Tell us about your project, goals, and timeline..."
                  ></textarea>
                  <div className="text-[9px] sm:text-[10px] text-gray-400 mt-1 text-right">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group w-full px-5 sm:px-6 py-3 sm:py-3.5 bg-[#0D0D0D] text-white text-xs sm:text-sm font-bold rounded-lg hover:bg-[#038C7F] transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <i className="ri-send-plane-line text-sm sm:text-base"></i>
                      </div>
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-2 sm:gap-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-xs sm:text-sm">
                    <i className="ri-checkbox-circle-line text-base sm:text-lg text-green-600 flex-shrink-0"></i>
                    <span>Thank you! We'll get back to you within 24 hours.</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 sm:gap-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm">
                    <i className="ri-error-warning-line text-base sm:text-lg text-red-600 flex-shrink-0"></i>
                    <span>Something went wrong. Please try again.</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
